import { useRef, useState } from 'react'
import { FORM_I18N } from '../data/form-i18n'
import { trackEvent } from '../lib/analytics'

const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit'
const REQUEST_TIMEOUT_MS = 15000

const LIMITS = {
  nameMin: 2,
  nameMax: 100,
  emailMax: 254,
  messageMin: 10,
  messageMax: 5000,
  companyMax: 100,
  phoneMax: 32,
  subjectSlice: 20,
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const EMPTY_FORM = Object.freeze({
  name: '',
  email: '',
  company: '',
  phone: '',
  message: '',
})

/** @typedef {{ name: string, email: string, company: string, phone: string, message: string }} ContactFormData */

/**
 * Validate the contact form fields against length and format rules.
 * Returns a map of field -> error key (empty object means valid). Error values
 * are stable keys the UI can render via FORM_I18N or inline copy.
 *
 * @param {ContactFormData} data
 * @returns {Record<string, string>}
 */
function validateFields(data) {
  const errors = {}
  const name = data.name.trim()
  if (name.length < LIMITS.nameMin || name.length > LIMITS.nameMax) {
    errors.name = 'name'
  }
  const email = data.email.trim()
  if (!EMAIL_RE.test(email) || email.length > LIMITS.emailMax) {
    errors.email = 'email'
  }
  const message = data.message.trim()
  if (message.length < LIMITS.messageMin || message.length > LIMITS.messageMax) {
    errors.message = 'message'
  }
  if (data.company.trim().length > LIMITS.companyMax) {
    errors.company = 'company'
  }
  if (data.phone.trim().length > LIMITS.phoneMax) {
    errors.phone = 'phone'
  }
  return errors
}

/**
 * Build the Web3Forms request payload, matching the legacy per-page shape.
 *
 * @param {Object} args
 * @param {string} args.accessKey
 * @param {ContactFormData} args.data
 * @param {string} args.captchaToken
 * @param {string} args.subjectPrefix
 * @returns {Record<string, string>}
 */
// Fallback text for empty optional fields, exactly as the per-locale inline
// forms sent it before they moved to this hook (inbox filters may key on it).
const EMPTY_FIELD_TEXT = Object.freeze({
  tr: { companySubject: 'Firma Belirtilmedi', field: 'Belirtilmedi' },
  en: { companySubject: 'Company Not Specified', field: 'Not Specified' },
  de: { companySubject: 'Firma nicht angegeben', field: 'Nicht angegeben' },
})

function buildPayload({ accessKey, data, captchaToken, subjectPrefix, lang = 'tr' }) {
  const empty = EMPTY_FIELD_TEXT[lang] || EMPTY_FIELD_TEXT.tr
  const base = `${data.name.slice(0, LIMITS.subjectSlice)} - ${data.company.slice(0, LIMITS.subjectSlice) || empty.companySubject
    }`
  const subject = subjectPrefix ? `${subjectPrefix} - ${base}` : base
  return {
    access_key: accessKey,
    subject,
    from_name: data.name,
    name: data.name,
    email: data.email,
    company: data.company || empty.field,
    phone: data.phone || empty.field,
    message: data.message,
    'h-captcha-response': captchaToken,
  }
}

/**
 * POST the payload to Web3Forms with a hard timeout.
 *
 * @param {Record<string, string>} payload
 * @returns {Promise<boolean>} Resolves true only when Web3Forms reports success.
 */
async function postToWeb3Forms(payload) {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
  try {
    const response = await fetch(WEB3FORMS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal,
    })
    const result = await response.json()
    return response.ok && result?.success === true
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Headless contact / RFQ form controller. Owns field state, validation, the
 * honeypot + hCaptcha anti-abuse gates, and the Web3Forms submission. All
 * failures are routed to component state — nothing is thrown to the caller and
 * nothing is written to the console.
 *
 * @param {Object} [options]
 * @param {'tr'|'en'|'de'} [options.lang='tr']
 * @param {string} [options.subjectPrefix=''] - Prepended to the email subject (e.g. service name).
 * @param {string} [options.service='general'] - Analytics service dimension.
 * @param {string} [options.formLocation='home_contact'] - Analytics form_location dimension.
 * @returns {{
 *   formData: ContactFormData,
 *   setField: (field: keyof ContactFormData, value: string) => void,
 *   formStatus: 'idle'|'sending'|'success'|'error',
 *   errors: Record<string, string>,
 *   captchaRef: import('react').RefObject<unknown>,
 *   onCaptchaVerify: (token: string) => void,
 *   onCaptchaExpire: () => void,
 *   honeypotProps: { name: string, value: string, onChange: (e: { target: { value: string } }) => void },
 *   handleSubmit: (e: { preventDefault: () => void }) => Promise<void>,
 *   reset: () => void,
 *   t: import('../data/form-i18n').FormLocaleStrings
 * }}
 */
export function useContactForm({
  lang = 'tr',
  subjectPrefix = '',
  service = 'general',
  formLocation = 'home_contact',
} = {}) {
  const [formData, setFormData] = useState({ ...EMPTY_FORM })
  const [formStatus, setFormStatus] = useState('idle')
  const [errors, setErrors] = useState({})
  const [captchaToken, setCaptchaToken] = useState(null)
  const [honeypot, setHoneypot] = useState('')
  const captchaRef = useRef(null)

  const t = FORM_I18N[lang] || FORM_I18N.tr

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev))
  }

  const resetCaptcha = () => {
    setCaptchaToken(null)
    if (captchaRef.current?.resetCaptcha) captchaRef.current.resetCaptcha()
  }

  const reset = () => {
    setFormData({ ...EMPTY_FORM })
    setErrors({})
    setHoneypot('')
    setFormStatus('idle')
    resetCaptcha()
  }

  const onCaptchaVerify = (token) => {
    setCaptchaToken(token)
    setErrors((prev) => (prev.captcha ? { ...prev, captcha: undefined } : prev))
  }

  const onCaptchaExpire = () => setCaptchaToken(null)

  const handleSubmit = async (e) => {
    e.preventDefault()

    const accessKey = import.meta.env.VITE_WEB3_ACCESS_KEY
    if (!accessKey) {
      setErrors({ config: 'config' })
      setFormStatus('error')
      return
    }

    // Honeypot: a filled hidden field means a bot. Pretend success, never POST.
    if (honeypot.trim() !== '') {
      reset()
      setFormStatus('success')
      return
    }

    const fieldErrors = validateFields(formData)
    if (!captchaToken) fieldErrors.captcha = 'captcha'
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors)
      setFormStatus('error')
      return
    }

    setErrors({})
    setFormStatus('sending')
    try {
      const payload = buildPayload({ accessKey, data: formData, captchaToken, subjectPrefix, lang })
      const ok = await postToWeb3Forms(payload)
      if (!ok) {
        setFormStatus('error')
        resetCaptcha()
        return
      }
      setFormStatus('success')
      setFormData({ ...EMPTY_FORM })
      resetCaptcha()
      trackEvent('generate_lead', { service, locale: lang, form_location: formLocation })
      trackEvent('form_submit', { service, locale: lang, form_location: formLocation })
    } catch {
      setFormStatus('error')
      resetCaptcha()
    }
  }

  const honeypotProps = {
    name: 'botcheck',
    value: honeypot,
    onChange: (e) => setHoneypot(e.target.value),
  }

  return {
    formData,
    setField,
    formStatus,
    errors,
    captchaRef,
    onCaptchaVerify,
    onCaptchaExpire,
    honeypotProps,
    handleSubmit,
    reset,
    t,
  }
}
